import { Trans, useLingui } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updatePlannedDischargeDateTime,
  usePatientInformationFields,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { dateInputSchema } from "@medoma/models/date-and-time";

export const EditPlannedDischargeDateTime = ({
  currentPlannedDischargeDateTime,
  onSuccess,
  patientId,
}: {
  currentPlannedDischargeDateTime: Date | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      plannedDischargeDateTime: dateInputSchema.encode(
        currentPlannedDischargeDateTime ?? null,
      ),
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (plannedDischargeDateTime: Date | null) => {
      return updatePlannedDischargeDateTime(
        patientId,
        plannedDischargeDateTime,
      );
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const {
    data: conditionalInformationFields,
    isPending: isPendingConditionalInformationFields,
    isError: isErrorConditionalInformationFields,
    error: errorConditionalInformationFields,
  } = usePatientInformationFields();

  if (isPendingConditionalInformationFields) {
    return <Loading message={t`Hämtar information om valbara fält`} />;
  }

  if (isErrorConditionalInformationFields) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om valbara fält.`} ${deducedError(errorConditionalInformationFields)}`}
      />
    );
  }

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(dateInputSchema.decode(formData.plannedDischargeDateTime));
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar planerad utskrivning`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <InputField
              label={t`Planerad utskrivning`}
              showOptionalLabel={
                conditionalInformationFields.PlannedDischargeDateTime ===
                informationFieldConfigValueSchema.enum.optional
              }
              type="date"
              outerWidth="fit"
              errorMessage={errors.plannedDischargeDateTime?.message}
              {...register("plannedDischargeDateTime", {
                required: {
                  value:
                    conditionalInformationFields.PlannedDischargeDateTime ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange planerad utskrivning`,
                },
              })}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
